import {
  ACTIVE_MATCH_MODE,
  AI,
  BALL,
  FIELD,
  GK,
  goalMouth,
  modeHasGoalkeeper,
  modeSpeed,
  dorsalGoalCoverWeight,
  dynamicOffsideLineX,
  HALFWAY_X,
  offsideLineX,
  offsideSpaceScore,
  PLAYER,
  playerRoleProfile,
  fieldPlayersPerTeam,
  ROLE_PROFILES,
  TACTICS,
  TEAM_INSTRUCTIONS,
  type Ball,
  type Player,
  type Possession,
  type TeamInstructions,
  type TeamPhase,
  type TeamSide,
  type TacticalRole,
  type Vec2,
} from "@golazo/shared";
import { clamp, vec, len2 } from "../core/math.js";
// Determinista entre motores: ver `core/detMath.ts`.
import { dexp, dsin } from "../core/detMath.js";
import { isInCrossingZone } from "../game/kick.js";
import { predictGoalLineCrossing } from "../physics/ballPrediction.js";
import { FORBIDDEN, solveAssignment } from "./assignment.js";
import { matchUrgency, situationalInstructions, type MatchClock } from "./situation.js";
import { anticipationScale } from "./skill.js";

const HALF_HEIGHT = FIELD.HEIGHT / 2;

export interface TeamTacticsState {
  phase: TeamPhase;
  /** El plan del equipo YA ajustado al marcador y al reloj (ver `urgency`). */
  instructions: TeamInstructions;
  /**
   * Lo que pide el partido: +1 ir a por el gol, -1 proteger, 0 jugar el plan.
   * Ver `ai/situation.ts`.
   */
  urgency: number;
  defensiveLineX: number;
  /** Linea de fuera de juego vista por el equipo atacante (actualizada cada tick). */
  offsideLineX: number;
  blockAnchor: Vec2;
  pressers: ReadonlySet<string>;
  marks: ReadonlyMap<string, string>;
  /** Quien esta llegando desde atras ahora mismo, y cuanto le queda. */
  lateRuns: ReadonlyMap<string, number>;
  /** Lateral/defensor siguiendo extremo rival. */
  tracks: ReadonlyMap<string, string>;
  counterPressLeft: number;
  /** Segundos que le quedan a la ventana de contraataque tras robar. */
  counterLeft: number;
  ownerId: string | null;
  /**
   * Quien dio el ultimo pase interno. Sirve para el tercer hombre y para no
   * devolver el muro A→B→A en cuanto se recibe.
   */
  lastPasserId: string | null;
  /** Ultimo hombre sin portero fijo: cubre la boca de gol voluntariamente. */
  goalCoverId: string | null;
  /** El que se queda arriba al defender (`instructions.outlet`). */
  outletId: string | null;
}

export type TacticsWorld = Record<TeamSide, TeamTacticsState>;

interface MutableTeamState {
  phase: TeamPhase;
  instructions: TeamInstructions;
  urgency: number;
  /** Donde se escribe el plan ajustado cuando la urgencia no es 0. */
  situational: TeamInstructions;
  defensiveLineX: number;
  offsideLineX: number;
  blockAnchor: Vec2;
  pressers: Set<string>;
  marks: Map<string, string>;
  tracks: Map<string, string>;
  counterPressLeft: number;
  counterLeft: number;
  /** Equipo del ultimo que tuvo el balon controlado (sobrevive al balon suelto). */
  lastHolderSide: TeamSide | null;
  /**
   * Segundos que la fase `attack` sobrevive con el balon suelto.
   *
   * Espejo de `counterPressLeft`: si perder el balon deja 2,4 s de transicion,
   * soltarlo en un pase no puede apagar el ataque en el mismo tick. Sin esto
   * el bloque solo empuja mientras alguien SUJETA el balon — medido: 19% del
   * tiempo, con el balon suelto el 63%.
   */
  attackHoldLeft: number;
  /** Segundos hasta el proximo recalculo de marcas. */
  markTimer: number;
  /** id → segundos que le quedan de llegada desde atras. */
  lateRuns: Map<string, number>;
  /** id → segundos de descanso antes de poder volver a llegar. */
  lateRunRest: Map<string, number>;
  /** Dueno del balon el tick anterior (cualquier equipo). */
  lastBallOwnerId: string | null;
  ownerId: string | null;
  lastOwnerId: string | null;
  lastPasserId: string | null;
  /** Ultimo companero con balon; sobrevive al instante en que el balon va suelto. */
  pendingPasserId: string | null;
  goalCoverId: string | null;
  outletId: string | null;
}

function dist(a: Vec2, b: Vec2): number {
  return len2(a.x - b.x, a.y - b.y);
}

function attackDir(side: TeamSide): number {
  return side === "home" ? 1 : -1;
}

function defendedGoalX(side: TeamSide): number {
  return side === "home" ? 0 : FIELD.WIDTH;
}

function teamPlayers(players: readonly Player[], side: TeamSide): Player[] {
  return players.filter((p) => p.side === side);
}

function outfield(players: readonly Player[], side: TeamSide, humanId: string | null): Player[] {
  return players.filter((p) => p.side === side && p.role !== "gk" && p.id !== humanId);
}

function roleProfile(player: Player) {
  return playerRoleProfile(player);
}

function clampX(x: number, player: Player): number {
  return clamp(x, player.radius, FIELD.WIDTH - player.radius);
}

function clampY(y: number, player: Player): number {
  return clamp(y, player.radius, FIELD.HEIGHT - player.radius);
}

/** Evita que las carreras largas se planten en posicion ilegal antes del pase. */
function offsideAwareX(
  player: Player,
  desiredX: number,
  ball: Ball,
  opponents: readonly Player[],
): number {
  // Con portero: es el penultimo ADVERSARIO el que traza la linea.
  const defenders = opponents;
  const dir = attackDir(player.side);
  const margin = TACTICS.LINE_TOLERANCE * 0.5;

  // Se usa la linea ESTATICA (balon / penultimo defensa) combinada con el medio
  // campo, NO `dynamicOffsideLineX`.
  //
  // La version dinamica devuelve el centro del campo mientras ningun atacante
  // haya cruzado, y eso aqui se muerde la cola: como esto es lo que decide si
  // alguien sube, nadie proponia cruzar, nadie cruzaba, y la linea seguia en el
  // centro. No es saltarse la regla — en cuanto el jugador cruza, la linea real
  // pasa a ser exactamente esta. Es no dejarse frenar por una condicion que el
  // propio movimiento elimina.
  //
  // El arbitraje real (`checkOffsideOnReceive`) no se toca: sigue usando la
  // version dinamica. Esto es solo la guia de colocacion de la IA.
  const staticLine = offsideLineX(player.side, ball.pos.x, defenders);
  const line = dir > 0 ? Math.max(HALFWAY_X, staticLine) : Math.min(HALFWAY_X, staticLine);
  if (dir > 0) return Math.min(desiredX, line - margin);
  return Math.max(desiredX, line + margin);
}

function average(nums: number[]): number {
  if (nums.length === 0) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/**
 * Linea de presion del equipo: hasta donde sale a apretar al portador, en
 * fraccion del campo desde su propia porteria (1 = en todo el campo). Ver
 * `TACTICS.ENGAGE_MIN`.
 */
export function engagementDepth(instructions: TeamInstructions): number {
  const t = clamp(
    (instructions.pressing - TACTICS.ENGAGE_LOW_PRESSING) /
      (TACTICS.ENGAGE_FULL_PRESSING - TACTICS.ENGAGE_LOW_PRESSING),
    0,
    1,
  );
  return TACTICS.ENGAGE_MIN + (1 - TACTICS.ENGAGE_MIN) * t;
}

/** Fraccion del campo entre `x` y la porteria que defiende `side`. */
function ownDepth(side: TeamSide, x: number): number {
  return attackDir(side) > 0 ? x / FIELD.WIDTH : (FIELD.WIDTH - x) / FIELD.WIDTH;
}

/** La `x` que queda a `depth` (fraccion del campo) de la porteria de `side`. */
function xAtDepth(side: TeamSide, depth: number): number {
  return attackDir(side) > 0 ? depth * FIELD.WIDTH : FIELD.WIDTH - depth * FIELD.WIDTH;
}

/**
 * El portador esta mas alla de nuestra linea de presion: no se sale a por el.
 * Con la linea en 1 (presion en todo el campo) no lo esta nunca.
 */
function beyondEngagement(side: TeamSide, carrier: Player, instructions: TeamInstructions): boolean {
  const line = engagementDepth(instructions);
  return line < 1 && ownDepth(side, carrier.pos.x) > line;
}

/**
 * Un plan defensivo que se va mas alla de la linea de presion se queda en
 * ella: el que marca espera al rival en su zona en vez de seguirle hasta el
 * area contraria. Es lo que da a un equipo que espera su bloque bajo.
 */
function holdEngagement(plan: OffBallPlan, player: Player, instructions: TeamInstructions): OffBallPlan {
  const line = engagementDepth(instructions);
  if (line >= 1) return plan;
  const margin =
    roleProfile(player).line === "att" ? TACTICS.ENGAGE_FORWARD_MARGIN : TACTICS.ENGAGE_MARK_MARGIN;
  const limit = Math.min(1, line + margin);
  if (ownDepth(player.side, plan.x) <= limit) return plan;
  return { ...plan, x: clampX(xAtDepth(player.side, limit), player) };
}

export function createTacticsWorld(options: { readScore?: boolean } = {}): {
  world: TacticsWorld;
  update: (
    players: readonly Player[],
    ball: Ball,
    possession: Possession,
    humanId: string | null,
    dt: number,
    clock?: MatchClock,
  ) => void;
} {
  // Solo las sondas lo apagan, para medir lo que cambia leer el marcador.
  const readScore = options.readScore !== false;
  const mutable: Record<TeamSide, MutableTeamState> = {
    home: freshMutable("home"),
    away: freshMutable("away"),
  };

  const world: TacticsWorld = {
    home: freezeTeam(mutable.home),
    away: freezeTeam(mutable.away),
  };

  return {
    world,
    update(players, ball, possession, humanId, dt, clock) {
      for (const side of ["home", "away"] as const) {
        const team = mutable[side];
        team.urgency = readScore && clock ? matchUrgency(side, clock) : 0;
        rebuildTeamState(team, side, players, ball, possession, humanId, dt);
        Object.assign(world[side], freezeTeam(mutable[side]));
      }
    },
  };
}

function freshMutable(side: TeamSide): MutableTeamState {
  return {
    phase: "loose",
    instructions: TEAM_INSTRUCTIONS[side],
    urgency: 0,
    situational: { ...TEAM_INSTRUCTIONS[side] },
    defensiveLineX: side === "home" ? FIELD.WIDTH * 0.18 : FIELD.WIDTH * 0.82,
    offsideLineX: HALFWAY_X,
    blockAnchor: vec(FIELD.WIDTH / 2, HALF_HEIGHT),
    pressers: new Set(),
    marks: new Map(),
    tracks: new Map(),
    counterPressLeft: 0,
    counterLeft: 0,
    lastHolderSide: null,
    attackHoldLeft: 0,
    markTimer: 0,
    lateRuns: new Map(),
    lateRunRest: new Map(),
    lastBallOwnerId: null,
    ownerId: null,
    lastOwnerId: null,
    lastPasserId: null,
    pendingPasserId: null,
    goalCoverId: null,
    outletId: null,
  };
}

function freezeTeam(state: MutableTeamState): TeamTacticsState {
  return {
    phase: state.phase,
    instructions: state.instructions,
    urgency: state.urgency,
    defensiveLineX: state.defensiveLineX,
    offsideLineX: state.offsideLineX,
    blockAnchor: state.blockAnchor,
    pressers: state.pressers,
    lateRuns: state.lateRuns,
    marks: state.marks,
    tracks: state.tracks,
    counterPressLeft: state.counterPressLeft,
    counterLeft: state.counterLeft,
    ownerId: state.ownerId,
    lastPasserId: state.lastPasserId,
    goalCoverId: state.goalCoverId,
    outletId: state.outletId,
  };
}

function rebuildTeamState(
  state: MutableTeamState,
  side: TeamSide,
  players: readonly Player[],
  ball: Ball,
  possession: Possession,
  humanId: string | null,
  dt: number,
): void {
  state.instructions = situationalInstructions(TEAM_INSTRUCTIONS[side], state.urgency, state.situational);
  state.pressers.clear();
  state.tracks.clear();

  const previousOwnerId = state.lastBallOwnerId;
  state.lastBallOwnerId = possession.ownerId;

  const owner = possession.ownerId
    ? (players.find((p) => p.id === possession.ownerId) ?? null)
    : null;
  const mates = teamPlayers(players, side);
  const opponents = players.filter((p) => p.side !== side);
  const outfieldMates = outfield(players, side, humanId);

  const nextOwnerId = owner?.side === side ? owner.id : null;

  // Al soltar el balon, recuerda quien lo tenia (el pase viaja suelto un instante).
  if (state.ownerId && !nextOwnerId) {
    state.pendingPasserId = state.ownerId;
  }

  // Recepcion de un companero: ese pending es el pasador (tercer hombre).
  if (nextOwnerId && state.pendingPasserId && state.pendingPasserId !== nextOwnerId) {
    state.lastPasserId = state.pendingPasserId;
    state.pendingPasserId = null;
  } else if (nextOwnerId && state.lastOwnerId && state.lastOwnerId !== nextOwnerId) {
    const prev = players.find((p) => p.id === state.lastOwnerId);
    if (prev?.side === side) state.lastPasserId = prev.id;
  }

  if (nextOwnerId) state.pendingPasserId = null;

  state.ownerId = nextOwnerId;

  if (state.lastOwnerId && !state.ownerId) {
    const lost = players.find((p) => p.id === state.lastOwnerId);
    // En 3v3 el contra-press metia a dos en el balon: un solo presser
    // ya basta; no hace falta fase "transition" larga.
    if (lost?.side === side) {
      state.counterPressLeft =
        fieldPlayersPerTeam() <= 3 ? 0 : TACTICS.COUNTER_PRESS_TIME;
    }
  }
  state.lastOwnerId = state.ownerId;
  state.counterPressLeft = Math.max(0, state.counterPressLeft - dt);

  // El ataque sobrevive al vuelo del balon. Un pase no es perder la iniciativa:
  // es justo el momento en que los companeros tienen que estar subiendo para
  // recibirlo. Con la fase apagandose en el tick del golpeo, `planShape` dejaba
  // de aplicar `attackPush` durante todo el trayecto del pase — y el balon esta
  // suelto el 63% del tiempo, asi que el bloque casi nunca empujaba.
  //
  // Solo cuenta el balon SUELTO: si lo captura el rival, la fase cae a
  // transicion/defensa en el acto, como antes.
  if (state.ownerId) state.attackHoldLeft = TACTICS.ATTACK_HOLD_TIME;
  else state.attackHoldLeft = Math.max(0, state.attackHoldLeft - dt);

  const previousPhase = state.phase;
  // Robado al rival: se abre la ventana de contra. Se mira quien la tuvo por
  // ULTIMA vez, no la fase del tick anterior: casi todo robo pasa por un
  // instante de balon suelto (la entrada la despeja y luego se recoge), y
  // mirando la fase la ventana solo se abria en el 9 % de las posesiones de 3v3.
  if (state.ownerId && state.lastHolderSide !== null && state.lastHolderSide !== side) {
    state.counterLeft = TACTICS.COUNTER_WINDOW;
  } else if (owner && owner.side !== side) {
    state.counterLeft = 0;
  } else {
    state.counterLeft = Math.max(0, state.counterLeft - dt);
  }
  if (owner) state.lastHolderSide = owner.side;
  if (state.ownerId) state.phase = "attack";
  else if (owner && owner.side !== side) {
    state.attackHoldLeft = 0;
    state.phase = state.counterPressLeft > 0 ? "transition" : "defend";
  } else if (state.attackHoldLeft > 0) state.phase = "attack";
  else state.phase = "loose";

  updateLateRuns(state, side, players, ball, dt);

  // Interrupcion "mid-slice": no espera al siguiente recalculo programado.
  //
  // Solo cuenta PERDER el balon. La tentacion es interrumpir en cualquier
  // cambio de posesion, pero el balon queda suelto y se recupera decenas de
  // veces por minuto: interrumpir con eso recalcula casi cada tick y anula el
  // time-slicing entero, que era el motivo de espaciarlo.
  const lostPossession = previousPhase === "attack" && state.phase !== "attack";
  void previousOwnerId;

  state.defensiveLineX = computeDefensiveLine(
    side,
    mates,
    ball,
    state.instructions,
    state.phase,
    owner,
  );
  state.blockAnchor = computeBlockAnchor(side, ball, state.defensiveLineX);

  state.offsideLineX = dynamicOffsideLineX(
    side,
    ball.pos.x,
    // CON portero: la linea la traza el penultimo ADVERSARIO, y el portero es
    // uno. Sin el, la linea salia un defensa por delante de donde debe estar.
    opponents,
    mates.filter((p) => p.role !== "gk"),
  );

  // El que se queda arriba no presiona, no cubre la porteria ni marca: se le
  // quita de la lista antes de repartir nada.
  assignOutlet(state, side, outfieldMates);
  // Con el balon suelto si puede ir a por el: sigue siendo el mas cercano.
  const defenders =
    state.outletId && state.phase !== "loose"
      ? outfieldMates.filter((p) => p.id !== state.outletId)
      : outfieldMates;
  assignPressers(state, owner, defenders, ball, humanId);
  assignGoalCover(state, side, defenders, humanId, ball);
  if (state.goalCoverId) state.pressers.delete(state.goalCoverId);
  assignMarks(state, side, opponents, owner, defenders, dt, lostPossession);
  assignTracks(state, opponents, defenders);
}

/**
 * Dejar a uno arriba (`outlet`): al defender, el delantero mas adelantado se
 * queda esperando la salida. Con uno solo de campo no hay a quien dejar; con
 * dos (el 3v3) se queda uno atras con el portero, que es justo la apuesta de la
 * contra. Se conserva el mismo mientras siga siendo valido, para que no se
 * turnen a cada tick.
 */
function assignOutlet(state: MutableTeamState, side: TeamSide, outfieldMates: readonly Player[]): void {
  // Tambien con el balon suelto: si no, en cada rechace volvia al bloque.
  if (state.phase === "attack" || state.instructions.outlet <= 0 || fieldPlayersPerTeam() < 2) {
    state.outletId = null;
    return;
  }
  if (state.outletId && outfieldMates.some((p) => p.id === state.outletId)) return;
  const dir = attackDir(side);
  const strikers = outfieldMates.filter((p) => p.tacticRole === "st");
  const pool = strikers.length > 0 ? strikers : outfieldMates;
  let best: Player | null = null;
  for (const p of pool) {
    if (!best || (p.pos.x - best.pos.x) * dir > 0) best = p;
  }
  state.outletId = best?.id ?? null;
}

function computeBlockAnchor(side: TeamSide, ball: Ball, lineX: number): Vec2 {
  const goalX = defendedGoalX(side);
  const dx = ball.pos.x - goalX;
  const dy = ball.pos.y - HALF_HEIGHT;
  const len = len2(dx, dy) || 1;
  const depth = Math.abs(lineX - goalX);
  return vec(goalX + (dx / len) * depth, HALF_HEIGHT + (dy / len) * depth);
}

/** Trampa de fuera de juego: subir el bloque ante pase largo inminente. */
function isOffsideTrapMoment(
  side: TeamSide,
  ball: Ball,
  owner: Player | null,
  phase: TeamPhase,
): boolean {
  if (phase !== "defend" && phase !== "loose") return false;

  const dir = attackDir(side);
  const towardOurGoal = -ball.vel.x * dir;
  if (!owner && towardOurGoal > TACTICS.OFFSIDE_TRAP_BALL_SPEED) return true;

  if (owner && owner.side !== side) {
    const oppOwnHalf =
      owner.side === "home" ? owner.pos.x < HALFWAY_X : owner.pos.x > HALFWAY_X;
    const staticBall = len2(ball.vel.x, ball.vel.y) < 55;
    const deepInOwnHalf =
      owner.side === "home"
        ? owner.pos.x < HALFWAY_X - FIELD.WIDTH * 0.12
        : owner.pos.x > HALFWAY_X + FIELD.WIDTH * 0.12;
    if (oppOwnHalf && staticBall && deepInOwnHalf) return true;
  }

  return false;
}

function computeDefensiveLine(
  side: TeamSide,
  mates: readonly Player[],
  ball: Ball,
  instructions: TeamInstructions,
  phase: TeamPhase,
  owner: Player | null,
): number {
  const dir = attackDir(side);
  const goalX = defendedGoalX(side);

  let lineHeight = instructions.lineHeight;
  if (isOffsideTrapMoment(side, ball, owner, phase)) {
    lineHeight = Math.min(1, lineHeight + TACTICS.OFFSIDE_TRAP_BOOST);
  }

  const outfield = mates.filter((p) => p.role !== "gk");
  const sorted = [...outfield].sort((a, b) =>
    dir > 0 ? b.pos.x - a.pos.x : a.pos.x - b.pos.x,
  );

  const trapLine =
    sorted.length >= 2
      ? sorted[1]!.pos.x
      : sorted.length === 1
        ? sorted[0]!.pos.x
        : goalX + dir * FIELD.WIDTH * 0.25;

  const cbs = mates.filter((p) => p.tacticRole === "cb");
  const spawnBase =
    cbs.length > 0
      ? average(cbs.map((p) => p.spawn.x))
      : goalX + dir * FIELD.WIDTH * 0.22;
  const cbMean = cbs.length > 0 ? average(cbs.map((p) => p.pos.x)) : trapLine;

  const trapBlend = trapLine * 0.62 + spawnBase * 0.38;
  const base = dir > 0 ? Math.max(trapBlend, cbMean) : Math.min(trapBlend, cbMean);

  const lineShift = (lineHeight - 0.5) * 150;
  const ballPull = (ball.pos.x - base) * 0.17 * dir;

  const minLine = goalX + dir * FIELD.WIDTH * 0.18;
  const maxLine = goalX + dir * FIELD.WIDTH * 0.5;

  return clamp(base + lineShift + ballPull, minLine, maxLine);
}

function assignPressers(
  state: MutableTeamState,
  owner: Player | null,
  outfieldMates: readonly Player[],
  ball: Ball,
  humanId: string | null,
): void {
  const { pressing } = state.instructions;
  const arcadeBoost = ACTIVE_MATCH_MODE.ai.allowBackwardPass ? 1 : 1.35;
  let maxPressers =
    state.phase === "transition"
      ? Math.min(2, TACTICS.MAX_PRESSERS)
      : Math.max(1, Math.round(pressing * TACTICS.MAX_PRESSERS * arcadeBoost));

  // 3v3/2v2: un solo jugador va al balon; el resto marca al que abre.
  // Sin esto los dos rivales enjambran al humano y nadie cubre el desmarque.
  const n = fieldPlayersPerTeam();
  // "Asfixiar" es la excepcion: dos al balon aunque deje libre a uno.
  if (n <= 3) maxPressers = pressing >= TACTICS.PRESS_DOUBLE_SMALL ? 2 : 1;
  else if (n <= 5) maxPressers = Math.min(maxPressers, 2);

  // Balon suelto: siempre hay alguien yendo a por el, mande la fase que mande.
  //
  // Antes esto miraba `phase === "loose"`, y con `ATTACK_HOLD_TIME` manteniendo
  // el ataque vivo durante el vuelo del balon la condicion dejaba de cumplirse
  // justo cuando mas falta hacia: la funcion salia por el `return` de abajo sin
  // asignar a nadie, y el balon suelto se quedaba sin dueno hasta que lo cogia
  // el rival.
  if (!owner) {
    const closest = nearestTo(ball.pos, outfieldMates);
    if (closest) state.pressers.add(closest.id);
    return;
  }

  if (state.phase !== "defend" && state.phase !== "transition") return;
  if (!owner) return;

  const candidates = [...outfieldMates].sort((a, b) => {
    const wa = roleProfile(a).pressWeight;
    const wb = roleProfile(b).pressWeight;
    return dist(a.pos, owner.pos) - dist(b.pos, owner.pos) + (wb - wa) * 40;
  });

  for (let i = 0; i < Math.min(maxPressers, candidates.length); i++) {
    state.pressers.add(candidates[i]!.id);
  }

  // leadPress masivo solo en plantillas grandes (si no, vuelve el enjambre).
  if (pressing >= 0.55 && n > 5) {
    for (const p of outfieldMates) {
      if (roleProfile(p).leadPress) state.pressers.add(p.id);
    }
  }

  if (humanId) state.pressers.delete(humanId);
}

/**
 * Sin portero fijo: elige un ultimo hombre que se queda cubriendo la meta.
 * No aplica en 1v1 (un solo jugador no puede quedarse atras siempre) ni en
 * modos con arquero dedicado.
 */
function ballInOurThird(ball: Ball, side: TeamSide): boolean {
  const dir = attackDir(side);
  return dir > 0 ? ball.pos.x < FIELD.WIDTH * 0.38 : ball.pos.x > FIELD.WIDTH * 0.62;
}

function assignGoalCover(
  state: MutableTeamState,
  side: TeamSide,
  outfieldMates: readonly Player[],
  humanId: string | null,
  ball: Ball,
): void {
  if (modeHasGoalkeeper() || outfieldMates.length <= 1) {
    state.goalCoverId = null;
    return;
  }

  // Con posesion propia nadie juega de arquero: el ultimo hombre sube con el resto.
  if (state.phase === "attack") {
    state.goalCoverId = null;
    return;
  }

  // En 3v3 no aparques al central en la meta a mitad de campo: debe marcar
  // al companero libre. Solo cubre boca si el balon amenaza el area.
  if (fieldPlayersPerTeam() <= 3 && !ballInOurThird(ball, side)) {
    state.goalCoverId = null;
    return;
  }

  const dir = attackDir(side);
  const depth = (p: Player): number => (dir > 0 ? p.pos.x : FIELD.WIDTH - p.pos.x);
  const roleRank = (p: Player): number => {
    const cover = dorsalGoalCoverWeight(p.shirtNumber);
    if (cover >= 2) return -2;
    if (cover > 0) return -1;
    return p.tacticRole === "cb" ? 0 : p.tacticRole === "fb" ? 1 : 2;
  };

  const candidates = outfieldMates
    .filter((p) => p.id !== humanId)
    .sort((a, b) => roleRank(a) - roleRank(b) || depth(a) - depth(b));

  if (candidates.length === 0) {
    state.goalCoverId = null;
    return;
  }

  const preferred =
    candidates.find((p) => !state.pressers.has(p.id) && roleRank(p) <= 1) ??
    candidates.find((p) => !state.pressers.has(p.id)) ??
    candidates[0]!;

  const current = state.goalCoverId
    ? candidates.find((p) => p.id === state.goalCoverId)
    : null;
  if (current && roleRank(current) <= roleRank(preferred) + 1) {
    state.goalCoverId = current.id;
    return;
  }

  state.goalCoverId = preferred.id;
}

/**
 * Peligro de un atacante, de 0 a 1: cuanto mas cerca de nuestra porteria y mas
 * centrado, mas urgente es que alguien lo tenga cogido.
 */
function threatDanger(threat: Player, goalX: number): number {
  const toGoal = dist(threat.pos, vec(goalX, HALF_HEIGHT));
  const proximity = clamp(1 - toGoal / (FIELD.WIDTH * 0.55), 0, 1);
  const central = clamp(1 - Math.abs(threat.pos.y - HALF_HEIGHT) / HALF_HEIGHT, 0, 1);
  return proximity * (0.6 + 0.4 * central);
}

/**
 * Reparto de marcas por metodo hungaro.
 *
 * El sistema anterior era codicioso: cada defensa (ordenados por cercania a su
 * porteria) se quedaba el primer atacante libre a menos de 420. Nunca duplicaba
 * marcas, pero el reparto podia ser pesimo — un defensa se llevaba al atacante
 * que le venia mejor a otro, y ese otro acababa cruzando medio campo. Medido:
 * 310 unidades de distancia media entre marcador y marcado.
 *
 * Ahora se minimiza el recorrido AGREGADO de toda la linea. Sobre el coste
 * (distancia) se aplican dos correcciones:
 *
 *  - **Peligro**: los atacantes amenazantes salen mas baratos, para que sean
 *    los que se cubren cuando hay menos defensas que rivales. El hungaro puro
 *    solo mira distancia y dejaria suelto al 9 dentro del area.
 *  - **Histeresis**: conservar tu marca actual sale mas barato. Sin esto, dos
 *    rivales cruzandose invierten los costes y los defensas se intercambian las
 *    marcas sin parar.
 */
function assignMarks(
  state: MutableTeamState,
  side: TeamSide,
  opponents: readonly Player[],
  owner: Player | null,
  defenders: readonly Player[],
  dt: number,
  interrupted: boolean,
): void {
  // Con el balon, el marcaje no aplica: se borra el plan.
  if (state.phase === "attack") {
    state.marks.clear();
    state.markTimer = 0;
    return;
  }

  // Balon suelto: se CONSERVA el reparto. Un rechace de tres decimas no es
  // motivo para rehacer el marcaje de toda la linea; si se borrase aqui, el
  // constante ir y venir de `defend` a `loose` reintroduciria el jitter por
  // la puerta de atras.
  if (state.phase === "loose") return;

  state.markTimer -= dt;
  if (state.markTimer > 0 && !interrupted) {
    // Entre recalculos se conservan las marcas, descartando las que ya no valen
    // (el marcado paso a llevar el balon, o alguien se fue del campo de vision).
    for (const [markerId, targetId] of state.marks) {
      const marker = defenders.find((p) => p.id === markerId);
      const target = opponents.find((o) => o.id === targetId);
      if (!marker || !target || target.id === owner?.id) state.marks.delete(markerId);
    }
    return;
  }
  state.markTimer = TACTICS.MARK_REFRESH;

  const goalX = defendedGoalX(side);
  // En lados pequenos no cuentes como amenaza a quien esta pegado al
  // portador: marcarlo es un segundo pressing disfrazado.
  const pocket =
    fieldPlayersPerTeam() <= 3 ? TACTICS.MIN_SUPPORT_DIST * 0.85 : 0;
  const threats = opponents.filter((o) => {
    if (o.role === "gk" || o.id === owner?.id) return false;
    if (pocket > 0 && owner && dist(o.pos, owner.pos) < pocket) return false;
    return true;
  });
  // En 3v3 los "ST" tambien marcan: si no, solo el CB podia y acababa en meta
  // o pressing, y el desmarque quedaba libre mientras dos iban al balon.
  const markMaxDist =
    fieldPlayersPerTeam() <= 3 ? TACTICS.MARK_MAX_DIST * 1.55 : TACTICS.MARK_MAX_DIST;
  const markers =
    fieldPlayersPerTeam() <= 3
      ? defenders.filter(
          (p) =>
            p.role !== "gk" &&
            !state.pressers.has(p.id) &&
            p.id !== state.goalCoverId,
        )
      : defenders.filter(
          (p) => p.tacticRole === "cb" || p.tacticRole === "fb" || p.tacticRole === "cm",
        );

  if (markers.length === 0 || threats.length === 0) {
    state.marks.clear();
    return;
  }

  const cost = markers.map((marker) =>
    threats.map((threat) => {
      const d = dist(marker.pos, threat.pos);
      if (d > markMaxDist) return FORBIDDEN;

      const markerSkill = roleProfile(marker);
      const danger = threatDanger(threat, goalX) * TACTICS.MARK_DANGER_WEIGHT;
      const sticky = state.marks.get(marker.id) === threat.id ? TACTICS.MARK_STICKY_BONUS : 0;
      // Anticipacion reduce el coste: defensas que leen el juego cierran antes.
      return d / markerSkill.anticipation - danger - sticky;
    }),
  );

  const assignment = solveAssignment(cost);

  state.marks.clear();
  for (let i = 0; i < assignment.length; i++) {
    const j = assignment[i]!;
    if (j < 0) continue;
    // El solver siempre reparte lo que puede; las celdas prohibidas se caen aqui.
    if (cost[i]![j]! >= FORBIDDEN) continue;
    state.marks.set(markers[i]!.id, threats[j]!.id);
  }
}

/** Laterales cierran el carril del extremo rival (FIFA track back). */
function assignTracks(
  state: MutableTeamState,
  opponents: readonly Player[],
  defenders: readonly Player[],
): void {
  if (state.phase !== "defend" && state.phase !== "transition") return;

  const wingers = opponents.filter((o) => o.tacticRole === "wm" || o.tacticRole === "fb");
  const fullbacks = defenders.filter((p) => p.tacticRole === "fb");

  const used = new Set<string>();
  for (const fb of fullbacks) {
    const sameSide = wingers
      .filter((w) => !used.has(w.id))
      .filter((w) => Math.sign(w.pos.y - HALF_HEIGHT) === Math.sign(fb.spawn.y - HALF_HEIGHT))
      .sort((a, b) => dist(a.pos, fb.pos) - dist(b.pos, fb.pos))[0];
    if (!sameSide) continue;
    used.add(sameSide.id);
    state.tracks.set(fb.id, sameSide.id);
  }
}

function nearestTo(target: Vec2, candidates: readonly Player[]): Player | null {
  let best: Player | null = null;
  let bestDist = Infinity;
  for (const p of candidates) {
    const d = dist(p.pos, target);
    if (d < bestDist) {
      best = p;
      bestDist = d;
    }
  }
  return best;
}

/**
 * Llegada desde atras: quien se suma al ataque y cuanto le dura.
 *
 * El bloque no puede avanzar mas de lo que avanza porque `offsideAwareX` lo
 * recorta contra la linea de fuera de juego, y eso es correcto. Esta es la via
 * que la regla SI permite: arrancar por detras del balon, donde el fuera de
 * juego no existe, y llegar al area cuando el balon ya esta arriba.
 *
 * Todo el criterio es de estado, sin azar: la simulacion es determinista y una
 * tirada aqui romperia el lockstep.
 */
function updateLateRuns(
  state: MutableTeamState,
  side: TeamSide,
  players: readonly Player[],
  ball: Ball,
  dt: number,
): void {
  for (const [id, left] of [...state.lateRuns]) {
    const next = left - dt;
    if (next > 0) state.lateRuns.set(id, next);
    else {
      state.lateRuns.delete(id);
      state.lateRunRest.set(id, TACTICS.LATE_RUN_COOLDOWN);
    }
  }
  for (const [id, rest] of [...state.lateRunRest]) {
    const next = rest - dt;
    if (next > 0) state.lateRunRest.set(id, next);
    else state.lateRunRest.delete(id);
  }

  const appetite = state.instructions.lateRuns;
  if (appetite <= 0 || state.phase !== "attack") return;
  if (state.lateRuns.size >= TACTICS.LATE_RUN_MAX_CONCURRENT) return;

  // El portador manda: sin balon arriba no hay a donde llegar.
  const carrier = state.ownerId
    ? (players.find((p) => p.id === state.ownerId) ?? null)
    : (state.pendingPasserId
        ? (players.find((p) => p.id === state.pendingPasserId) ?? null)
        : null);
  if (!carrier || carrier.side !== side) return;

  const dir = attackDir(side);
  const goalX = side === "home" ? FIELD.WIDTH : 0;
  const carrierAdvance = 1 - Math.abs(goalX - carrier.pos.x) / FIELD.WIDTH;
  // Cuanto mas se pide, antes se arranca.
  const threshold = TACTICS.LATE_RUN_CARRIER_ADVANCE - appetite * 0.14;
  if (carrierAdvance < threshold) return;

  let best: Player | null = null;
  let bestBehind = Infinity;
  for (const p of players) {
    if (p.side !== side || p.role === "gk") continue;
    if (p.id === carrier.id) continue;
    if (!roleProfile(p).lateRun) continue;
    if (state.lateRuns.has(p.id) || state.lateRunRest.has(p.id)) continue;

    // Por DETRAS del balon: es lo que hace la carrera legal.
    const behind = (ball.pos.x - p.pos.x) * dir;
    if (behind < TACTICS.LATE_RUN_MIN_BEHIND || behind > TACTICS.LATE_RUN_MAX_BEHIND) continue;

    // El mas cercano al balon es el que mejor llega; el id desempata para que
    // la decision sea identica en todos los clientes.
    if (behind < bestBehind || (behind === bestBehind && best && p.id < best.id)) {
      best = p;
      bestBehind = behind;
    }
  }

  if (best) state.lateRuns.set(best.id, TACTICS.LATE_RUN_TIME);
}

/**
 * Plan del que llega: al area, por su carril, sin cruzarse con el portador.
 *
 * Sigue pasando por `offsideAwareX`, y esa es la gracia: mientras el balon
 * avanza, el que llega avanza con el; cuando el balon se para, el se planta a
 * su altura. Es una llegada al area, no una carrera al fuera de juego.
 */
function planLateRun(
  player: Player,
  ball: Ball,
  carrier: Player | null,
  opponents: readonly Player[],
): OffBallPlan {
  const dir = attackDir(player.side);
  const goalX = player.side === "home" ? FIELD.WIDTH : 0;
  const boxEdge = goalX - dir * FIELD.BOX_WIDTH * 0.75;

  // Se separa del portador para ofrecer una opcion distinta, no una sombra.
  const laneBias = carrier ? Math.sign(player.pos.y - carrier.pos.y) || 1 : 1;
  const y = clampY(
    HALF_HEIGHT + laneBias * FIELD.BOX_HEIGHT * 0.3,
    player,
  );

  return {
    x: clampX(offsideAwareX(player, boxEdge, ball, opponents), player),
    y,
    arrive: 0,
    exact: false,
  };
}

export interface OffBallPlan {
  x: number;
  y: number;
  arrive: number;
  exact: boolean;
  /**
   * Carrera COMPROMETIDA: gana al triangulo de apoyo.
   *
   * Por defecto `planTriangleSupport` tiene prioridad sobre `planAttackRun`, y
   * con razon para el apoyo normal. Pero una llegada al segundo palo no es un
   * apoyo: es lo contrario, alejarse del balon para atacar un espacio. Sin esta
   * marca el plan se calculaba y se tiraba, y el jugador volvia a orbitar al
   * portador como todos los demas.
   */
  committed?: boolean;
}

export function planOffBall(
  player: Player,
  ball: Ball,
  owner: Player | null,
  mates: readonly Player[],
  opponents: readonly Player[],
  team: TeamTacticsState,
): OffBallPlan {
  const plan = planOffBallCore(player, ball, owner, mates, opponents, team);
  /*
   * Defendiendo, nadie se va mas alla de la linea de presion: ni el que marca
   * ni el que se coloca en el bloque. Es lo que hace que un equipo que espera
   * espere de verdad. El que presiona ya la respeta el solo (temporiza si el
   * portador esta mas alla), y el que se queda arriba esta para eso.
   *
   * Con el balon SUELTO y lejos (mas alla de la linea) tampoco se sube: el que
   * va a por el ya esta asignado, y el resto no. Sin esto el bloque se colocaba
   * como en ataque durante los rechaces —casi la mitad del partido— y cuando el
   * rival la cogia no le daba tiempo a volver: el cerrojo defendia al 51 %.
   */
  const defending = team.phase === "defend" || team.phase === "transition";
  // Los de arriba si pelean el rechace lejano: estan ahi para eso.
  const waitingLoose =
    team.phase === "loose" &&
    roleProfile(player).line !== "att" &&
    ownDepth(player.side, ball.pos.x) > engagementDepth(team.instructions);
  if (
    (defending || waitingLoose) &&
    !team.pressers.has(player.id) &&
    player.id !== team.outletId &&
    player.id !== team.goalCoverId
  ) {
    return holdEngagement(plan, player, team.instructions);
  }
  return plan;
}

function planOffBallCore(
  player: Player,
  ball: Ball,
  owner: Player | null,
  mates: readonly Player[],
  opponents: readonly Player[],
  team: TeamTacticsState,
): OffBallPlan {
  const profile = roleProfile(player);
  const instructions = team.instructions;

  const isPresser = team.pressers.has(player.id);
  const isGoalCover = player.id === team.goalCoverId;
  const markId = team.marks.get(player.id);
  const marked = markId ? (opponents.find((o) => o.id === markId) ?? null) : null;
  const trackId = team.tracks.get(player.id);
  const tracked = trackId ? (opponents.find((o) => o.id === trackId) ?? null) : null;

  if (isGoalCover && ballInOurThird(ball, player.side)) {
    return planGoalCover(player, ball, owner, mates);
  }

  if (player.id === team.outletId && (team.phase === "defend" || team.phase === "transition")) {
    return planOutlet(player, ball, opponents, instructions);
  }

  // Balon SIN dueno: el recuperador va a por el, mande la fase que mande.
  //
  // Antes esto miraba `phase === "loose"`, que equivalia a "sin dueno" porque
  // la fase de ataque moria en el tick del golpeo. Con `ATTACK_HOLD_TIME` el
  // ataque sobrevive al vuelo del balon, y esa condicion dejaba de cumplirse
  // justo cuando el balon estaba suelto: nadie iba a recuperarlo. Medido: la
  // posesion cayo del 37% al 28% y el hold maximo de 3,0 s a 1,6 s.
  if (isPresser && !owner) {
    const intercept = interceptPoint(player, ball);
    return { x: intercept.x, y: intercept.y, arrive: 0, exact: true };
  }

  if (isPresser && team.phase !== "attack") {
    if (owner) {
      // Mas alla de la linea de presion no se sale: se temporiza delante del
      // bloque, entre el portador y la porteria, hasta que la cruce.
      if (beyondEngagement(player.side, owner, instructions)) {
        return planHoldEngagement(player, owner, instructions);
      }
      return planPress(player, owner, ball);
    }
    return { x: ball.pos.x, y: ball.pos.y, arrive: 0, exact: true };
  }

  if (marked && team.phase === "defend") {
    return planMarkShadow(player, marked);
  }

  // 3v3: si no hay marca hungara (distancia, timer…), el no-presser
  // igual debe ir al libre, no al bloque del balon.
  if (
    team.phase === "defend" &&
    !isPresser &&
    !isGoalCover &&
    fieldPlayersPerTeam() <= 3 &&
    owner
  ) {
    const pocket = TACTICS.MIN_SUPPORT_DIST * 0.85;
    const alreadyMarked = new Set(team.marks.values());
    const free = opponents
      .filter(
        (o) =>
          o.role !== "gk" &&
          o.id !== owner.id &&
          !alreadyMarked.has(o.id) &&
          dist(o.pos, owner.pos) >= pocket,
      )
      .sort((a, b) => dist(player.pos, a.pos) - dist(player.pos, b.pos))[0];
    if (free) return planMarkShadow(player, free);
    // Sin desmarque real: corta el pase, no dobles al portador.
    return planPassingLane(player, owner);
  }

  if (tracked && team.phase === "defend" && !isPresser) {
    const goalX = defendedGoalX(player.side);
    return {
      x: tracked.pos.x + (goalX - tracked.pos.x) * 0.28,
      y: tracked.pos.y,
      arrive: 20,
      exact: false,
    };
  }

  /*
   * El que llega desde atras, antes que nada.
   *
   * Va FUERA del bloque que exige `team.ownerId` a proposito: el balon esta
   * suelto dos tercios del tiempo, y una llegada tiene que seguir corriendo
   * MIENTRAS el pase viaja — ese es justo el momento en que se llega. Con la
   * comprobacion dentro, el corredor volvia a su sitio en cuanto salia el pase
   * y se quedaba 225 unidades por detras del balon.
   */
  if (team.phase === "attack" && (team.lateRuns.get(player.id) ?? 0) > 0) {
    // Solo para elegir carril: si el balon va suelto, no hay portador y se
    // elige un lado por defecto.
    const runCarrier =
      (team.ownerId ? (mates.find((m) => m.id === team.ownerId) ?? null) : null) ??
      (owner && owner.side === player.side ? owner : null);
    if (!runCarrier || player.id !== runCarrier.id) {
      return planLateRun(player, ball, runCarrier, opponents);
    }
  }

  if (team.phase === "attack") {
    const farPost = planFarPostRun(player, ball, profile, instructions, opponents);
    if (farPost) return farPost;
  }

  /*
   * Y al area, con balon o sin el.
   *
   * Va FUERA del bloque que exige `team.ownerId` a proposito, igual que la
   * llegada desde atras: al area se entra MIENTRAS el centro viaja, y en ese
   * momento el balon no tiene dueño. Con la comprobacion dentro, el delantero se
   * daba media vuelta justo cuando salia el pase.
   *
   * Tampoco exige fase de "attack" a secas: basta con no estar defendiendo y que
   * el balon este arriba (lo decide `planBoxAttack`). El balon va suelto el 41-46%
   * del tiempo y la fase de ataque solo cubre el 40-47%; entre las dos rendijas
   * se colaba justo la situacion que importa.
   */
  if (team.phase !== "defend") {
    const box = planBoxAttack(player, ball, profile, mates, opponents, owner?.id ?? team.ownerId);
    if (box) return box;

  }

  if (team.phase === "attack" && team.ownerId) {
    const carrier = mates.find((m) => m.id === team.ownerId) ?? owner;
    if (carrier && player.id !== carrier.id) {
      const fieldMates = mates.filter((m) => m.role !== "gk");
      // Rivales CON portero: es el penultimo adversario el que traza la linea.
      const space = offsideSpaceScore(
        player.pos.x,
        player.side,
        ball.pos.x,
        opponents,
        fieldMates,
      );
      if (space <= 0.05 && (profile.runInBehind || player.tacticRole === "st")) {
        const line = team.offsideLineX;
        const dir = attackDir(player.side);
        const retreatX =
          dir > 0 ? line - TACTICS.LINE_TOLERANCE : line + TACTICS.LINE_TOLERANCE;
        return {
          x: clampX(retreatX, player),
          y: player.pos.y,
          arrive: 14,
          exact: false,
        };
      }

      const triangle = planTriangleSupport(
        player,
        carrier,
        mates,
        opponents,
        ball,
        instructions,
      );
      const run = planAttackRun(
        player,
        carrier,
        ball,
        profile,
        instructions,
        mates,
        opponents,
      );

      // Una carrera comprometida manda sobre el apoyo: el que ataca el segundo
      // palo no esta orbitando al portador, esta yendose de el.
      if (run?.committed) return run;

      // Lados pequenos: geometria por rol (CB pivote, ST carril), no apiñarse.
      if (fieldPlayersPerTeam() <= 3) {
        if (triangle) return triangle;
        if (run) return run;
        return planShape(player, ball, team, profile, instructions, mates);
      }

      if (profile.runInBehind && run && inFinalThird(carrier, player.side)) {
        return withAttackDepth(run, player, profile, ball, opponents);
      }
      // El apoyo del interior se sesga hacia el hueco entre lineas rivales: es
      // la unica pieza del ataque que se coloca mirando a la defensa contraria
      // y no al portador.
      if (triangle) {
        const base = withAttackDepth(triangle, player, profile, ball, opponents);
        return withBetweenLines(base, player, ball, mates, opponents);
      }
      if (run) {
        const base = withAttackDepth(run, player, profile, ball, opponents);
        return withBetweenLines(base, player, ball, mates, opponents);
      }
    }
  }

  /*
   * Y aqui es donde de verdad importa el hueco entre lineas.
   *
   * Medido: el interior sale por `planShape` entre el 43% (5v5) y el 81%
   * (11v11) de sus decisiones. Todos los planes de ataque de arriba exigen
   * `phase === "attack"` CON dueno, y esa fase es minoria — el balon va suelto
   * el 39-46% del tiempo. Sesgar solo el apoyo del portador era sesgar el
   * rincon del partido en el que el medio casi nunca esta.
   *
   * Sobre la forma del bloque el sesgo significa lo que tiene que significar:
   * la posicion por defecto del interior deja de mirar solo al balon y empieza
   * a mirar tambien donde estan las lineas del rival.
   */
  return withBetweenLines(
    planShape(player, ball, team, profile, instructions, mates),
    player,
    ball,
    mates,
    opponents,
  );
}

/**
 * Sesgo de profundidad ofensiva sobre un plan de ataque.
 *
 * Todo el ataque (`planTriangleSupport`, `planAttackRun`) se coloca RELATIVO al
 * portador: vertices a ~105 u, apoyos a ~145. Con seis jugadores orbitando al
 * que lleva el balon, el equipo entero cabe en un radio de 150 y nadie ocupa el
 * campo rival — medido: 0,2 de 6 jugadores en el ultimo tercio, y el balon
 * viviendo en el centro (x=792).
 *
 * Esto anade la componente que faltaba: un empujon hacia la porteria rival que
 * NO depende de donde este el balon, escalado por el `attackPush` del rol (el
 * central apenas se mueve, el delantero mucho) y acotado por el fuera de juego.
 * El apoyo cercano se conserva; lo que cambia es que el bloque ofensivo respira
 * hacia delante en vez de apelotonarse.
 */
function withAttackDepth(
  plan: OffBallPlan,
  player: Player,
  profile: (typeof ROLE_PROFILES)[TacticalRole],
  ball: Ball,
  opponents: readonly Player[],
): OffBallPlan {
  if (profile.attackPush <= 0) return plan;
  const dir = attackDir(player.side);
  const pushed = plan.x + dir * profile.attackPush * TACTICS.ATTACK_DEPTH_BIAS;
  return {
    ...plan,
    x: clampX(offsideAwareX(player, pushed, ball, opponents), player),
  };
}

function supportScale(): number {
  const n = fieldPlayersPerTeam();
  if (n <= 2) return TACTICS.SMALL_SIDE_SUPPORT_SCALE * 1.15;
  if (n <= 3) return TACTICS.SMALL_SIDE_SUPPORT_SCALE;
  // 5v5: un poco mas de aire; 7+/11v11 sin tocar la geometria clasica.
  if (n <= 5) return 1.2;
  return 1;
}

function teammateSeparation(): number {
  const n = fieldPlayersPerTeam();
  if (n <= 3) return TACTICS.TEAMMATE_SEPARATION * TACTICS.SMALL_SIDE_SEP_SCALE;
  if (n <= 5) return TACTICS.TEAMMATE_SEPARATION * 1.35;
  return TACTICS.TEAMMATE_SEPARATION;
}

/** Empuja el objetivo lejos del portador hasta una distancia minima de pase. */
function enforceMinSupportDist(target: Vec2, carrier: Player, side: TeamSide): void {
  const minDist = TACTICS.MIN_SUPPORT_DIST * (fieldPlayersPerTeam() <= 3 ? 1 : 0.85);
  const dx = target.x - carrier.pos.x;
  const dy = target.y - carrier.pos.y;
  const d = len2(dx, dy);
  if (d >= minDist) return;
  if (d < 1e-3) {
    const dir = attackDir(side);
    target.x = carrier.pos.x + dir * minDist * 0.55;
    target.y = carrier.pos.y + (target.y < HALF_HEIGHT ? -1 : 1) * minDist * 0.55;
    return;
  }
  const s = minDist / d;
  target.x = carrier.pos.x + dx * s;
  target.y = carrier.pos.y + dy * s;
}

/** Separarse de otros companeros en el punto objetivo (no solo en la posicion actual). */
function separateFromMates(target: Vec2, player: Player, mates: readonly Player[]): void {
  const sep = teammateSeparation();
  for (const mate of mates) {
    if (mate.id === player.id || mate.role === "gk") continue;
    const dx = target.x - mate.pos.x;
    const dy = target.y - mate.pos.y;
    const d = len2(dx, dy);
    if (d > 1 && d < sep) {
      const push = (sep - d) / sep;
      target.x += (dx / d) * push * (sep * 0.45);
      target.y += (dy / d) * push * (sep * 0.45);
    }
  }
}

function inFinalThird(carrier: Player, side: TeamSide): boolean {
  const goalX = side === "home" ? FIELD.WIDTH : 0;
  return Math.abs(carrier.pos.x - goalX) < FIELD.WIDTH * 0.28;
}

/**
 * Juego de posicion: ocupa un vertice del triangulo alrededor del portador.
 * En 3v3/2v2: CB = pivote atras, ST = medio espacio adelantado (abre linea).
 * Nunca dos puntas delante pegadas al humano.
 */
function planTriangleSupport(
  player: Player,
  carrier: Player,
  mates: readonly Player[],
  opponents: readonly Player[],
  ball: Ball,
  instructions: TeamInstructions,
): OffBallPlan | null {
  if (fieldPlayersPerTeam() <= 3) {
    return planSmallSideSupport(player, carrier, mates, opponents, ball, instructions);
  }

  const dir = attackDir(player.side);
  const tempo = instructions.tempo;
  const widthMul = 0.75 + instructions.width * 0.45;
  const scale = supportScale();

  const candidates = mates
    .filter((m) => m.id !== carrier.id && m.role !== "gk")
    .sort((a, b) => {
      const roleW = (r: TacticalRole): number =>
        r === "cm" ? 0 : r === "wm" || r === "fb" ? 8 : r === "st" ? 20 : 30;
      return (
        dist(a.pos, carrier.pos) +
        roleW(a.tacticRole) -
        (dist(b.pos, carrier.pos) + roleW(b.tacticRole))
      );
    })
    .slice(0, TACTICS.TRIANGLE_SLOTS);

  const slot = candidates.findIndex((m) => m.id === player.id);
  if (slot < 0) return null;

  const ahead = TACTICS.TRIANGLE_AHEAD * (0.8 + tempo * 0.35) * scale;
  const wide = TACTICS.TRIANGLE_WIDTH * widthMul * scale;
  const pivot = TACTICS.TRIANGLE_PIVOT * (0.85 + tempo * 0.2) * scale;

  /*
   * Carril del apoyo: banda o medio espacio, segun el modo Y el rol.
   *
   * Un extremo (`wm`) o un lateral (`fb`) que sube en un modo con bandas se va
   * a la LINEA, no al medio espacio. Es lo que crea la posicion desde la que
   * se puede centrar — y sin ello la IA no centraba nunca, porque la zona de
   * centro empieza a 175 de la banda y el medio espacio esta a 320.
   *
   * En los modos de sala (hasta 4 de campo) nadie pisa la linea: ahi el juego
   * es por dentro, con paredes, y un extremo abierto solo seria un jugador
   * fuera del partido.
   */
  const wideRole = player.tacticRole === "wm" || player.tacticRole === "fb";
  const flanks = fieldPlayersPerTeam() >= TACTICS.FLANK_MIN_OUTFIELD;
  const ownSide = Math.sign(player.spawn.y - FIELD.HEIGHT / 2) || 1;
  const laneBias =
    wideRole && flanks
      ? (ownSide < 0 ? TACTICS.WIDE_LANES[0]! : TACTICS.WIDE_LANES[1]!)
      : TACTICS.HALF_SPACE_LANES[slot % 2]!;
  const halfSpaceY = FIELD.HEIGHT * laneBias;

  const slots: Vec2[] = [
    vec(carrier.pos.x + dir * ahead, carrier.pos.y - wide),
    vec(carrier.pos.x + dir * ahead * 0.9, carrier.pos.y + wide),
    vec(carrier.pos.x - dir * pivot, carrier.pos.y * 0.55 + halfSpaceY * 0.45),
  ];

  const target = slots[slot]!;
  if (slot < 2) {
    // El extremo se pega a SU carril mucho mas que un interior: si se le
    // arrastra hacia el portador deja de estar en banda, y entonces no hay
    // centro que valga.
    const pull = wideRole && flanks ? 0.78 : 0.35;
    target.y = target.y * (1 - pull) + halfSpaceY * pull;
  }

  return finalizeSupportTarget(player, carrier, mates, opponents, ball, target);
}

/**
 * 3v3/2v2: abrir el campo. El central queda de salida atras; el delantero
 * ocupa el medio espacio de su lado, lejos del portador (pase diagonal).
 */
function planSmallSideSupport(
  player: Player,
  carrier: Player,
  mates: readonly Player[],
  opponents: readonly Player[],
  ball: Ball,
  instructions: TeamInstructions,
): OffBallPlan | null {
  const dir = attackDir(player.side);
  const scale = supportScale();
  const ahead = TACTICS.TRIANGLE_AHEAD * (0.95 + instructions.tempo * 0.25) * scale;
  const wide = TACTICS.TRIANGLE_WIDTH * (0.9 + instructions.width * 0.35) * scale;
  const pivot = TACTICS.TRIANGLE_PIVOT * 1.35 * scale;

  const spawnSide = Math.sign(player.spawn.y - HALF_HEIGHT) || 1;
  const halfLane =
    spawnSide < 0 ? TACTICS.HALF_SPACE_LANES[0]! : TACTICS.HALF_SPACE_LANES[1]!;
  const halfY = FIELD.HEIGHT * halfLane;

  let target: Vec2;

  if (player.tacticRole === "cb") {
    const lateral = (carrier.pos.y + halfY) * 0.5;
    target = vec(carrier.pos.x - dir * pivot, lateral);
  } else {
    /*
     * Si el que lleva el balon se abre a una banda, el apoyo se va a la
     * CONTRARIA.
     *
     * Antes el apoyo se anclaba a su propio carril de salida, asi que si el
     * humano se iba por la izquierda podia acabar en ese mismo lado: dos
     * jugadores en la misma banda y el campo entero libre al otro. Abrirse a
     * la contraria estira a la defensa y deja el cambio de juego disponible,
     * que en 3v3 y 4v4 es practicamente la unica forma de romper un bloque.
     *
     * Solo cuando el portador esta DE VERDAD escorado: con el balon en el
     * centro cada uno se queda en su carril, que es lo correcto.
     */
    const carrierSide = Math.sign(carrier.pos.y - HALF_HEIGHT) || 1;
    const carrierWide = Math.abs(carrier.pos.y - HALF_HEIGHT) > FIELD.HEIGHT * 0.16;
    const farLane =
      carrierSide < 0 ? TACTICS.HALF_SPACE_LANES[1]! : TACTICS.HALF_SPACE_LANES[0]!;

    let y = carrierWide ? FIELD.HEIGHT * farLane : halfY;
    if (Math.abs(y - carrier.pos.y) < wide * 0.55) {
      y = carrier.pos.y + (carrierWide ? -carrierSide : spawnSide) * wide;
    }
    const other = mates.find(
      (m) => m.id !== player.id && m.id !== carrier.id && m.role !== "gk",
    );
    if (other && Math.abs(other.pos.y - y) < teammateSeparation() * 0.7) {
      y = FIELD.HEIGHT - y;
    }
    target = vec(carrier.pos.x + dir * ahead, y);
  }

  return finalizeSupportTarget(player, carrier, mates, opponents, ball, target);
}

function finalizeSupportTarget(
  player: Player,
  carrier: Player,
  mates: readonly Player[],
  opponents: readonly Player[],
  ball: Ball,
  target: Vec2,
): OffBallPlan | null {
  if (fieldPlayersPerTeam() <= 5) {
    enforceMinSupportDist(target, carrier, player.side);
    separateFromMates(target, player, mates);
  }

  const fieldMates = mates.filter((m) => m.role !== "gk");
  const space = offsideSpaceScore(
    target.x,
    player.side,
    ball.pos.x,
    opponents,
    fieldMates,
  );
  if (space <= 0.02) return null;
  target.x = offsideAwareX(player, target.x, ball, opponents);

  // Tras el clamp de fuera de juego, reasegura distancia lateral al portador.
  if (fieldPlayersPerTeam() <= 3) {
    const dy = target.y - carrier.pos.y;
    if (Math.abs(dy) < TACTICS.TRIANGLE_WIDTH * 0.55) {
      const side = Math.sign(dy) || Math.sign(player.spawn.y - HALF_HEIGHT) || 1;
      target.y = carrier.pos.y + side * TACTICS.TRIANGLE_WIDTH * supportScale() * 0.7;
    }
  }

  return {
    x: clampX(target.x, player),
    y: clampY(target.y, player),
    arrive: 18,
    exact: false,
  };
}

function planMarkShadow(player: Player, marked: Player): OffBallPlan {
  const goalX = defendedGoalX(player.side);
  return {
    x: marked.pos.x + (goalX - marked.pos.x) * TACTICS.MARK_SHADOW,
    y: marked.pos.y + (HALF_HEIGHT - marked.pos.y) * 0.12,
    arrive: 22,
    exact: false,
  };
}

/** Corta la linea de pase hacia meta sin sumarse al pressing del portador. */
function planPassingLane(player: Player, owner: Player): OffBallPlan {
  const goalX = defendedGoalX(player.side);
  const sideBias = Math.sign(player.spawn.y - HALF_HEIGHT) || 1;
  return {
    x: owner.pos.x + (goalX - owner.pos.x) * 0.38,
    y: clampY(owner.pos.y + sideBias * 95, player),
    arrive: 24,
    exact: false,
  };
}

function planPress(player: Player, owner: Player, ball: Ball): OffBallPlan {
  const goalX = defendedGoalX(player.side);
  const dx = goalX - owner.pos.x;
  const dy = HALF_HEIGHT - owner.pos.y;
  const len = len2(dx, dy) || 1;

  if (dist(player.pos, owner.pos) < 95) {
    return { x: ball.pos.x, y: ball.pos.y, arrive: 0, exact: true };
  }

  return {
    x: owner.pos.x + (dx / len) * 36,
    y: owner.pos.y + (dy / len) * 36,
    arrive: 14,
    exact: false,
  };
}

/**
 * Temporizar: el portador esta mas alla de la linea de presion. Se espera en la
 * linea, en el camino del portador a la porteria, cerrando el centro y sin
 * salir a por el. En cuanto cruza, `planPress` toma el relevo.
 */
function planHoldEngagement(player: Player, owner: Player, instructions: TeamInstructions): OffBallPlan {
  const goalX = defendedGoalX(player.side);
  const lineX = xAtDepth(player.side, engagementDepth(instructions));
  const dx = owner.pos.x - goalX;
  const t = Math.abs(dx) > 1 ? clamp((lineX - goalX) / dx, 0, 1) : 0;
  return {
    x: clampX(lineX, player),
    y: clampY(HALF_HEIGHT + (owner.pos.y - HALF_HEIGHT) * t, player),
    arrive: 18,
    exact: false,
  };
}

/**
 * El que se queda arriba (`outlet`): no baja a defender. Espera por delante,
 * en el carril del lado del balon, legal respecto al fuera de juego, para que
 * al robar haya a quien darsela de primeras.
 */
function planOutlet(
  player: Player,
  ball: Ball,
  opponents: readonly Player[],
  instructions: TeamInstructions,
): OffBallPlan {
  const depth =
    TACTICS.OUTLET_DEPTH_MIN +
    (TACTICS.OUTLET_DEPTH_MAX - TACTICS.OUTLET_DEPTH_MIN) * clamp(instructions.outlet, 0, 1);
  const x = offsideAwareX(player, xAtDepth(player.side, depth), ball, opponents);
  const lane = ball.pos.y < HALF_HEIGHT ? TACTICS.HALF_SPACE_LANES[0]! : TACTICS.HALF_SPACE_LANES[1]!;
  return {
    x: clampX(x, player),
    y: clampY(FIELD.HEIGHT * lane, player),
    arrive: 20,
    exact: false,
  };
}

/** Ultimo hombre: se planta en la linea de gol como un portero voluntario. */
function planGoalCover(
  player: Player,
  ball: Ball,
  owner: Player | null,
  mates: readonly Player[],
): OffBallPlan {
  const goalX = defendedGoalX(player.side);
  const goal = vec(goalX, HALF_HEIGHT);
  const dir = attackDir(player.side);
  const mouth = goalMouth();
  const lineX = goalX + dir * AI.GK_LINE_DEPTH;
  const ballDist = dist(player.pos, ball.pos);
  const ballSpeed = len2(ball.vel.x, ball.vel.y);
  const threat = !owner || owner.side !== player.side;

  if (threat && ballSpeed >= GK.THREAT_MIN_SPEED && ballDist < AI.GK_REACT_DISTANCE) {
    const crossing = predictGoalLineCrossing(ball, goalX);
    if (crossing !== null) {
      return {
        x: lineX,
        y: clamp(crossing, mouth.top, mouth.bottom),
        arrive: 0,
        exact: true,
      };
    }
  }

  if (threat && ballDist < AI.GK_RUSH_DISTANCE) {
    const closest = nearestTo(ball.pos, mates.filter((m) => m.role !== "gk"));
    if (closest?.id === player.id) {
      return { x: ball.pos.x, y: ball.pos.y, arrive: 0, exact: true };
    }
  }

  const dx = ball.pos.x - goal.x;
  const dy = ball.pos.y - goal.y;
  const len = len2(dx, dy) || 1;

  return {
    x: lineX,
    y: clamp(goal.y + (dy / len) * AI.GK_LINE_DEPTH * 0.85, mouth.top, mouth.bottom),
    arrive: 14,
    exact: false,
  };
}

/**
 * Llegada al SEGUNDO PALO desde el lado contrario al balon.
 *
 * Con el balon en una banda, el carril del segundo palo es el espacio mas
 * grande y mas libre del campo, y nadie lo atacaba. Dos causas, las dos
 * medidas:
 *
 * 1. El desdoble (`profile.overlap`) solo se dispara con el balon en TU banda,
 *    asi que el ancho del otro lado no tenia ninguna instruccion ofensiva.
 * 2. `planShape` —que es lo que coloca a casi todo el mundo casi todo el
 *    tiempo— desliza el bloque HACIA el balon (`BLOCK_SLIDE_Y`), o sea que el
 *    del lado contrario se iba hacia la banda del balon en vez de atacar el
 *    palo largo. El bloque avanza en formacion y nadie lo rompe.
 *
 * Va en `planOffBall` y NO dentro de `planAttackRun`: esa solo se llama con
 * fase de ataque Y portador propio, y medido con una sonda son 7 llamadas por
 * cada 90 ticks. Una version anterior vivia ahi y movia al jugador 2 unidades.
 *
 * La condicion es el CARRIL, no el rol: en 5v5 la formacion es portero + CB +
 * dos CM + ST y no hay ningun `fb`, asi que atarlo a `overlap` lo dejaba muerto
 * en el modo del que venia la queja.
 */
/**
 * Cuantos entran al area. El resto sostiene: alguien tiene que estar para el
 * rechace, y vaciar el medio campo por meter a todos dentro es como se encajan
 * las contras.
 */
function boxRunners(): number {
  const n = fieldPlayersPerTeam();
  /*
   * En lados chicos NADIE acampa en el area.
   *
   * Con dos jugadores de campo, mandar al delantero a esperar dentro le saca de
   * la jugada entera: no hay quien construya, el balon no sube, y no llega
   * nunca. Medido: el area pasaba de tener a alguien el 10% del tiempo al 5%,
   * o sea que la "mejora" la empeoraba a la mitad. Ahi manda la geometria de
   * triangulo que ya existe (`planTriangleSupport`), que es justo la buena para
   * tres contra tres.
   */
  if (n <= 2) return 0;
  // Dos hasta futbol 7 (mitad del ataque dentro, mitad sosteniendo) y tres de
  // ahi en adelante. Con uno solo el area se vuelve a quedar sola en cuanto le
  // toca presionar: medido, el 5v5 caia de 0,55 a 0,28 jugadores dentro.
  if (n <= 6) return 2;
  return 3;
}

/** Cuantos se ofrecen entre lineas a la vez. */
function betweenRunners(): number {
  const n = fieldPlayersPerTeam();
  // Con cuatro de campo o menos, quitar a uno del juego es quedarse sin juego.
  // 5v5 tiene CUATRO de campo: excluirlo dejaba fuera al modo por defecto.
  if (n <= 3) return 0;
  return 1;
}

/**
 * El hueco entre la defensa rival y su medio campo, si es que hay uno util.
 *
 * Es el sitio donde se recibe de cara, y de donde nace todo pase en
 * profundidad. Sin nadie ahi, el balon solo puede ir en horizontal o en largo.
 * Medido antes de esto: el hueco existia (217-399 u de ancho) pero solo lo
 * pisaba alguien LIBRE el 24-44% de los ticks con balon, y de rebote — ningun
 * plan apuntaba ahi.
 *
 * La razon era estructural: TODO el ataque se coloca relativo al PORTADOR
 * (`planTriangleSupport` a 105-145 u de el, `planAttackRun` a 45-200), asi que
 * el bloque orbita el balon y la posicion respecto al RIVAL no la mira nadie.
 * Esta es la primera funcion que mira a la defensa contraria.
 *
 * Devuelve solo el PUNTO. Que se hace con el lo decide `withBetweenLines`, y
 * hay una razon medida para esa separacion: el primer intento devolvia un plan
 * entero que sustituia al apoyo, y hundio el balon en el ultimo tercio del
 * 11v11 del 44% al 22% de los ticks. Un interior que abandona el triangulo de
 * apoyo para plantarse en el hueco deja al portador sin a quien darsela, y como
 * todo el ataque se coloca relativo al balon, se lleva al bloque entero con el.
 *
 * Las lineas se leen del PERFIL de rol (`line: def|mid|att`), no de la x: asi
 * significa lo mismo en 5v5 que en 11v11. Los modos sin medios (2v2-4v4) salen
 * por `null` solos.
 */
export function betweenLinesPocket(
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
): Vec2 | null {
  const slots = betweenRunners();
  if (slots === 0) return null;
  /*
   * Solo el interior, NO el extremo.
   *
   * El primer intento admitia a todo `line: "mid"`, y en 11v11 eso metia a los
   * dos extremos por dentro: el equipo se quedaba sin amplitud y el balon
   * dejaba de subir (58% -> 44% de balon en el ultimo tercio). Entre lineas se
   * recibe por dentro; por fuera se da anchura, y no las hace el mismo jugador.
   */
  if (player.tacticRole !== "cm") return null;

  const dir = attackDir(player.side);
  const goalX = player.side === "home" ? FIELD.WIDTH : 0;
  const advance = 1 - Math.abs(goalX - ball.pos.x) / FIELD.WIDTH;
  if (advance < TACTICS.BETWEEN_TRIGGER_ADVANCE) return null;
  // Mas arriba manda `planBoxAttack`: ya no se recibe, se remata.
  if (advance > TACTICS.BETWEEN_MAX_ADVANCE) return null;

  const rivals = opponents.filter((o) => o.role !== "gk");
  const defX = average(
    rivals.filter((o) => ROLE_PROFILES[o.tacticRole].line === "def").map((o) => o.pos.x),
  );
  const midX = average(
    rivals.filter((o) => ROLE_PROFILES[o.tacticRole].line === "mid").map((o) => o.pos.x),
  );
  if (defX === 0 || midX === 0) return null;

  const gap = (defX - midX) * dir;
  if (gap < TACTICS.BETWEEN_MIN_GAP) return null;

  /*
   * `player` va EXPLICITO: `mates` llega sin el (el cerebro lo filtra con
   * `p !== player`). Buscarse a uno mismo en esa lista no encuentra nunca nada
   * y la funcion devolveria `null` siempre — el fallo que ya costo una tarde
   * con la llegada al area.
   */
  const candidates = [player, ...mates].filter((m) => m.tacticRole === "cm");
  /*
   * No hace falta reservar un pivote. Con el plan independiente si hacia falta
   * —el que se iba al hueco desaparecia del apoyo— pero esto es un sesgo
   * acotado: el interior sigue en la estructura, solo que colocado en el
   * bolsillo. Reservarlo dejaba al 7v7 sin nadie, porque solo tiene un `cm`.
   */
  const going = Math.min(slots, candidates.length);
  if (going <= 0) return null;

  const eligible = candidates
    .sort((a, b) => {
      // El mas adelantado se ofrece primero; el id desempata para que los dos
      // clientes del online repartan igual.
      const da = (a.pos.x - midX) * dir;
      const db = (b.pos.x - midX) * dir;
      if (db !== da) return db - da;
      return a.id.localeCompare(b.id);
    })
    .slice(0, going);
  if (!eligible.some((m) => m.id === player.id)) return null;

  let x = midX + dir * gap * TACTICS.BETWEEN_DEPTH;

  /*
   * A tiro de pase. Es el limite mas duro de todo el ataque y no lo miraba
   * nadie: el balon raso alcanza ~440 unidades utiles en 11v11 sobre un campo
   * de 1600. Ofrecerse donde el balon no llega es quitarse del partido.
   */
  if (Math.abs(x - ball.pos.x) > TACTICS.BETWEEN_MAX_FROM_BALL) {
    const pulled = ball.pos.x + Math.sign(x - ball.pos.x) * TACTICS.BETWEEN_MAX_FROM_BALL;
    if ((pulled - midX) * dir < TACTICS.BETWEEN_MIN_GAP * 0.5) return null;
    x = pulled;
  }

  /*
   * Y por DELANTE del balon. El hueco entre las lineas rivales existe estes
   * donde estes: si el rival acaba de atacar, su bloque esta metido en NUESTRO
   * campo y el hueco cae detras del balon. Recibir entre lineas es progresar;
   * si no adelanta, no es eso.
   */
  if ((x - ball.pos.x) * dir < TACTICS.BETWEEN_MIN_AHEAD) return null;

  /*
   * El carril: los dos semiespacios y el centro. Se elige el mas LIBRE, no el
   * mas cercano — un cuerpo en el hueco con un central encima no es un
   * desmarque, es un pasajero.
   */
  const lanes = [
    FIELD.HEIGHT * TACTICS.HALF_SPACE_LANES[0]!,
    HALF_HEIGHT,
    FIELD.HEIGHT * TACTICS.HALF_SPACE_LANES[1]!,
  ];
  const roomAt = (y: number): number =>
    Math.min(...rivals.map((o) => len2(o.pos.x - x, o.pos.y - y)));

  // Reparto entero en cada jugador, para que todos lleguen al mismo resultado.
  const taken = new Set<number>();
  let mine = HALF_HEIGHT;
  for (const runner of eligible) {
    let bestLane = -1;
    let bestRoom = -Infinity;
    for (let i = 0; i < lanes.length; i++) {
      if (taken.has(i)) continue;
      // A igual espacio, el carril mas cercano al que corre: no se cruzan.
      const room = roomAt(lanes[i]!) - Math.abs(lanes[i]! - runner.pos.y) * 0.35;
      if (room > bestRoom) {
        bestRoom = room;
        bestLane = i;
      }
    }
    if (bestLane < 0) break;
    taken.add(bestLane);
    if (runner.id === player.id) mine = lanes[bestLane]!;
  }

  return vec(x, mine);
}

/**
 * Sesga un plan de apoyo hacia el hueco entre lineas, SIN sacar al jugador de
 * la estructura.
 *
 * El desplazamiento esta acotado (`BETWEEN_MAX_SHIFT`) y nunca tira hacia
 * atras. Esa es toda la diferencia con el primer intento, que devolvia un plan
 * propio: aqui el interior sigue siendo una opcion de pase cercana, solo que se
 * coloca en el bolsillo en vez de en un angulo de apoyo cualquiera.
 */
function withBetweenLines(
  plan: OffBallPlan,
  player: Player,
  ball: Ball,
  mates: readonly Player[],
  opponents: readonly Player[],
): OffBallPlan {
  const pocket = betweenLinesPocket(player, ball, mates, opponents);
  if (!pocket) return plan;
  const dir = attackDir(player.side);
  const shift = TACTICS.BETWEEN_MAX_SHIFT;
  const dx = clamp((pocket.x - plan.x) * dir, 0, shift) * dir;
  const dy = clamp(pocket.y - plan.y, -shift, shift);
  return {
    ...plan,
    x: clampX(offsideAwareX(player, plan.x + dx, ball, opponents), player),
    y: clampY(plan.y + dy, player),
  };
}

/**
 * OCUPAR EL AREA.
 *
 * El agujero mas viejo del ataque: con el balon en el ultimo tercio el area
 * rival estaba vacia el 69-90% del tiempo, y de ahi que el 68-84% de los tiros
 * salieran de fuera — no se disparaba de lejos por gusto, es que no habia a
 * quien pasarsela dentro.
 *
 * Y lo que lo impedia NO era el fuera de juego. Medido con `pnpm box:probe`:
 * con el balon arriba la linea cae dentro del area (x=1373-1459, el area empieza
 * en 1340) y el mas adelantado se quedaba en 1226-1274. Sobraban 104-208
 * unidades de sitio LEGAL sin usar. Simplemente no habia ningun plan que dijera
 * "entra".
 *
 * Va en `planOffBall` y NO en `planAttackRun` —que se ejecuta 7 veces por cada
 * 90 ticks— y sin exigir `team.ownerId`: el balon esta suelto la mitad del
 * tiempo, y al area se entra MIENTRAS el centro viaja, que es justo cuando no
 * hay dueño.
 *
 * El reparto de estaciones es determinista (ordenado por `id` en los empates)
 * porque esto corre dentro de la simulacion: dos clientes que repartieran
 * distinto jugarian partidos distintos.
 */
export function planBoxAttack(
  player: Player,
  ball: Ball,
  profile: (typeof ROLE_PROFILES)[TacticalRole],
  mates: readonly Player[],
  opponents: readonly Player[],
  /** El que lleva el balon: no entra al area, asi que no ocupa plaza. */
  ownerId: string | null = null,
): OffBallPlan | null {
  if (profile.attackPush < TACTICS.BOX_ATTACK_PUSH) return null;

  const dir = attackDir(player.side);
  const goalX = player.side === "home" ? FIELD.WIDTH : 0;
  // Cuanto ha recorrido el balon hacia la porteria rival, 0..1.
  const advance = 1 - Math.abs(goalX - ball.pos.x) / FIELD.WIDTH;
  if (advance < TACTICS.BOX_TRIGGER_ADVANCE) return null;

  /*
   * Quien entra: los de mas deber, y a igualdad el que ya esta mas adelantado.
   * Se ordena tambien por `id` para romper empates de forma estable — sin eso,
   * dos maquinas podrian repartir las estaciones al reves.
   */
  /*
   * `player` va EXPLICITO en la lista.
   *
   * `mates` llega sin el —el cerebro lo filtra con `p !== player`— asi que
   * buscarse a uno mismo ahi no encuentra nunca nada y la funcion devolvia
   * `null` siempre. Costo una tarde: la sonda daba numeros identicos al byte y
   * parecia que algo aguas abajo tiraba el plan, cuando el plan no llegaba a
   * existir.
   */
  /*
   * Sin el portador: el que va a centrar no remata su propio centro. Contaba,
   * y en 5v5 con el delantero en la banda su plaza se perdia: entraba uno solo
   * y el area estaba vacia el 82 % de las veces que el balon llegaba al fondo.
   */
  const eligible = [player, ...mates]
    .filter(
      (m) =>
        m.role !== "gk" &&
        (TACTICS.BOX_SKIP_OWNER <= 0 || m.id !== ownerId) &&
        roleProfile(m).attackPush >= TACTICS.BOX_ATTACK_PUSH,
    )
    .sort((a, b) => {
      const pa = roleProfile(a).attackPush;
      const pb = roleProfile(b).attackPush;
      if (pa !== pb) return pb - pa;
      const aa = (a.pos.x - goalX) * -dir;
      const ab = (b.pos.x - goalX) * -dir;
      if (aa !== ab) return ab - aa;
      return a.id.localeCompare(b.id);
    })
    .slice(0, boxRunners());

  if (eligible.length === 0 || !eligible.some((m) => m.id === player.id)) return null;

  /*
   * Estaciones repartidas por CARRIL, no por rol: se ordenan los que entran por
   * su `y` actual y se les dan las estaciones en el mismo orden. Asi nadie cruza
   * por delante de otro para llegar a su sitio, que es como se pisan las
   * carreras y acaban los dos en el mismo punto.
   */
  const byLane = [...eligible].sort((a, b) => a.pos.y - b.pos.y || a.id.localeCompare(b.id));
  const slot = byLane.findIndex((m) => m.id === player.id);
  const spread = TACTICS.BOX_STATION_SPREAD;
  const offsets =
    byLane.length <= 1 ? [0] : byLane.length === 2 ? [-spread, spread] : [-spread, 0, spread];
  const offset = offsets[Math.min(slot, offsets.length - 1)] ?? 0;

  // El del medio ataca el punto de penalti (mas atras); los de los palos, la
  // linea. Es el reparto de siempre de un centro: primer palo, punto, segundo.
  const depth = offset === 0 ? TACTICS.BOX_SPOT_DEPTH : TACTICS.BOX_POST_DEPTH;
  const targetX = goalX - dir * depth;

  return {
    // El clamp de fuera de juego sigue puesto: aqui hay sitio legal de sobra,
    // pero si la defensa sube, esto es lo que evita regalar la jugada.
    x: clampX(offsideAwareX(player, targetX, ball, opponents), player),
    y: clampY(HALF_HEIGHT + offset, player),
    arrive: 16,
    exact: false,
    committed: true,
  };
}

export function planFarPostRun(
  player: Player,
  ball: Ball,
  profile: (typeof ROLE_PROFILES)[TacticalRole],
  instructions: TeamInstructions,
  opponents: readonly Player[],
): OffBallPlan | null {
  if (!profile.lateRun || instructions.lateRuns <= 0) return null;

  const spawnOffset = player.spawn.y - HALF_HEIGHT;
  if (Math.abs(spawnOffset) <= FIELD.HEIGHT * TACTICS.FAR_POST_MIN_LANE) return null;

  const ballOffset = ball.pos.y - HALF_HEIGHT;
  if (Math.abs(ballOffset) <= FIELD.HEIGHT * TACTICS.FAR_POST_BALL_WIDE) return null;

  const spawnSide = Math.sign(spawnOffset) || 1;
  if (spawnSide === (Math.sign(ballOffset) || 1)) return null;

  // Se mide con el BALON y no con el portador: la llegada tiene que seguir
  // corriendo mientras el centro viaja, que es justo cuando se llega.
  const dir = attackDir(player.side);
  const goalX = player.side === "home" ? FIELD.WIDTH : 0;
  const advance = 1 - Math.abs(goalX - ball.pos.x) / FIELD.WIDTH;
  if (advance < TACTICS.FAR_POST_ADVANCE) return null;

  const mouth = goalMouth();
  const farY =
    HALF_HEIGHT + spawnSide * Math.min(mouth.half * TACTICS.FAR_POST_Y, FIELD.BOX_HEIGHT * 0.45);

  return {
    x: clampX(offsideAwareX(player, goalX - dir * TACTICS.FAR_POST_DEPTH, ball, opponents), player),
    y: clampY(farY, player),
    arrive: 18,
    exact: false,
    committed: true,
  };
}

function planAttackRun(
  player: Player,
  carrier: Player,
  ball: Ball,
  profile: (typeof ROLE_PROFILES)[TacticalRole],
  instructions: TeamInstructions,
  mates: readonly Player[],
  opponents: readonly Player[],
): OffBallPlan | null {
  const dir = attackDir(player.side);
  const forward = (carrier.pos.x - player.pos.x) * dir;
  const inFinalThird =
    Math.abs(carrier.pos.x - (player.side === "home" ? FIELD.WIDTH : 0)) < FIELD.WIDTH * 0.28;

  if (profile.runInBehind) {
    if (inFinalThird && forward > -30) {
      // Centro inminente desde banda: el 9 ataca el poste lejano (Q-value de timing).
      if (
        player.tacticRole === "st" &&
        isInCrossingZone(carrier) &&
        Math.abs(carrier.pos.x - (player.side === "home" ? FIELD.WIDTH : 0)) <
          FIELD.WIDTH * 0.32
      ) {
        const mouth = goalMouth();
        const farSide =
          Math.sign(player.pos.y - HALF_HEIGHT) !==
          Math.sign(carrier.pos.y - HALF_HEIGHT);
        const postY = farSide
          ? player.pos.y < HALF_HEIGHT
            ? mouth.top + 16
            : mouth.bottom - 16
          : player.pos.y < HALF_HEIGHT
            ? mouth.top + 10
            : mouth.bottom - 10;
        const goalX = player.side === "home" ? FIELD.WIDTH : 0;
        return {
          x: clampX(goalX - dir * 52, player),
          y: clampY(postY, player),
          arrive: 14,
          exact: false,
        };
      }

      const depth = TACTICS.RUN_BEHIND_PUSH * (0.65 + instructions.mentality * 0.35);
      const lateral = (player.spawn.y - HALF_HEIGHT) * instructions.width * 0.35;
      return {
        x: clampX(offsideAwareX(player, carrier.pos.x + dir * depth, ball, opponents), player),
        y: clampY(HALF_HEIGHT + lateral, player),
        arrive: 20,
        exact: false,
      };
    }
    // Fuera del area: en lados pequenos abrir carril; en 11v11 el 9 sigue bajando a apoyar.
    if (!inFinalThird) {
      if (fieldPlayersPerTeam() <= 3) {
        const scale = supportScale();
        const spawnSide = Math.sign(player.spawn.y - HALF_HEIGHT) || 1;
        const halfLane =
          spawnSide < 0 ? TACTICS.HALF_SPACE_LANES[0]! : TACTICS.HALF_SPACE_LANES[1]!;
        let y = FIELD.HEIGHT * halfLane;
        const wide = TACTICS.TRIANGLE_WIDTH * scale * 0.85;
        if (Math.abs(y - carrier.pos.y) < wide * 0.5) {
          y = carrier.pos.y + spawnSide * wide;
        }
        const target = vec(carrier.pos.x + dir * (75 * scale), y);
        enforceMinSupportDist(target, carrier, player.side);
        separateFromMates(target, player, mates);
        return {
          x: clampX(offsideAwareX(player, target.x, ball, opponents), player),
          y: clampY(target.y, player),
          arrive: 22,
          exact: false,
        };
      }
      // El 9 se queda POR DELANTE del balon durante la construccion.
      //
      // Antes bajaba a apoyar (`carrier.pos.x - dir * 90`), y eso dejaba al
      // equipo sin ninguna referencia ofensiva por delante del balon: no habia
      // a quien progresar, asi que el balon no salia del centro, asi que el
      // portador nunca alcanzaba el ultimo tercio, asi que la carrera en
      // profundidad de mas arriba (gateada por `inFinalThird`) no se activaba
      // jamas. Circulo cerrado — medido: el balon en fase de ataque vivia en
      // x=792 (el centro) y solo 0,2 jugadores de 6 pisaban el tercio rival.
      //
      // `offsideAwareX` es lo que lo hace sostenible: da profundidad sin
      // regalar el fuera de juego.
      const buildupDepth = TACTICS.RUN_AHEAD_BUILDUP * (0.7 + instructions.mentality * 0.3);
      return {
        x: clampX(
          offsideAwareX(player, carrier.pos.x + dir * buildupDepth, ball, opponents),
          player,
        ),
        y: clampY(carrier.pos.y + (player.spawn.y - HALF_HEIGHT) * 0.25, player),
        arrive: 22,
        exact: false,
      };
    }
  }

  if (profile.overlap) {
    const onSide = Math.sign(player.spawn.y - HALF_HEIGHT) === Math.sign(ball.pos.y - HALF_HEIGHT);

    if (onSide && forward > 20) {
      const wideY =
        player.spawn.y +
        (player.spawn.y - HALF_HEIGHT) * instructions.width * profile.widthFactor * 0.25;
      return {
        x: clampX(carrier.pos.x + dir * TACTICS.OVERLAP_PUSH, player),
        y: clampY(wideY, player),
        arrive: 22,
        exact: false,
      };
    }
  }

  if (player.tacticRole === "wm") {
    const wideY = HALF_HEIGHT + (player.spawn.y - HALF_HEIGHT) * (0.85 + instructions.width * 0.35);
    return {
      x: clampX(carrier.pos.x + dir * (120 + instructions.mentality * 80), player),
      y: clampY(wideY, player),
      arrive: 24,
      exact: false,
    };
  }

  if (player.tacticRole === "cm") {
    const supportRank = mates
      .filter((m) => m.role !== "gk" && m.id !== carrier.id)
      .sort((a, b) => dist(a.pos, carrier.pos) - dist(b.pos, carrier.pos))
      .indexOf(player);

    if (supportRank >= 0 && supportRank < 2) {
      const angle = supportRank === 0 ? -0.55 : 0.55;
      const offset = 130 + instructions.tempo * 40;
      return {
        x: clampX(carrier.pos.x + dir * offset * 0.35, player),
        y: clampY(carrier.pos.y + dsin(angle) * offset, player),
        arrive: 20,
        exact: false,
      };
    }
  }

  return null;
}

/**
 * Cuanto acompaña al balon un jugador de este rol, como fraccion de lo que
 * avanza el balon. Es la idea de DEBER de Football Manager dicha con el
 * vocabulario que ya existia: el central (`attackPush` 0,18) apenas se suma,
 * el delantero (0,72) sigue al balon casi entero.
 *
 * Esta fuera de `planShape` para poder probarla sola. La version anterior del
 * test lo media jugando 90 s y comparando medias, y eso resulto no medir esto:
 * cambiar el tamaño de las porterias o el ritmo del juego movia el numero
 * varias decimas sin que nadie tocara el bloque, porque cambia cuanto dura la
 * fase de ataque y con ello que trozo de la jugada entra en la media.
 */
export function attackSlide(attackPush: number, attacking: boolean): number {
  if (!attacking) return TACTICS.BLOCK_SLIDE_X;
  return TACTICS.BLOCK_SLIDE_X + attackPush * TACTICS.ATTACK_SLIDE_BONUS;
}

function planShape(
  player: Player,
  ball: Ball,
  team: TeamTacticsState,
  profile: (typeof ROLE_PROFILES)[TacticalRole],
  instructions: TeamInstructions,
  mates: readonly Player[],
): OffBallPlan {
  const dir = attackDir(player.side);
  const defending = team.phase === "defend" || team.phase === "transition";

  let x: number;
  let y: number;

  if (defending) {
    const goalX = defendedGoalX(player.side);
    const axisDx = ball.pos.x - goalX;
    const axisDy = ball.pos.y - HALF_HEIGHT;
    const axisLen = len2(axisDx, axisDy) || 1;
    const perpX = -axisDy / axisLen;
    const perpY = axisDx / axisLen;
    const lateral =
      (player.spawn.y - HALF_HEIGHT) * (0.42 + instructions.lineLength * 0.58);
    const roleDepth =
      profile.line === "def"
        ? 0
        : profile.line === "mid"
          ? -dir * 70
          : -dir * 130;

    x = team.blockAnchor.x + perpX * lateral + roleDepth;
    y = team.blockAnchor.y + perpY * lateral;
  } else {
    // En ataque el bloque sigue al balon MAS, y cuanto mas segun el rol. Con el
    // coeficiente unico la brecha crecia con cada metro que avanzaba el balon.
    const slide = attackSlide(profile.attackPush, team.phase === "attack");
    x =
      player.spawn.x +
      (ball.pos.x - FIELD.WIDTH / 2) * slide * (0.7 + instructions.mentality * 0.3);
    y =
      player.spawn.y +
      (ball.pos.y - HALF_HEIGHT) * TACTICS.BLOCK_SLIDE_Y * (0.55 + instructions.width * 0.45);
  }

  if (profile.widthFactor > 0.6 && !defending) {
    y = HALF_HEIGHT + (player.spawn.y - HALF_HEIGHT) * (0.75 + instructions.width * 0.55);
  }

  // Juego posicional: CM/ST ocupan medios espacios, no el eje ni la banda.
  if (team.phase === "attack" && (player.tacticRole === "cm" || player.tacticRole === "st")) {
    const lane =
      player.spawn.y < HALF_HEIGHT
        ? TACTICS.HALF_SPACE_LANES[0]!
        : TACTICS.HALF_SPACE_LANES[1]!;
    y = y * 0.45 + FIELD.HEIGHT * lane * 0.55;
  }

  if (team.phase === "defend" || team.phase === "transition") {
    y += (ball.pos.y - HALF_HEIGHT) * TACTICS.BALL_SIDE_SHIFT * instructions.compactness * 0.35;
  }

  // Anti-amontonamiento: separarse de companeros cercanos.
  const sep = teammateSeparation();
  const pushAmp = fieldPlayersPerTeam() <= 3 ? sep * 0.55 : 40;
  for (const mate of mates) {
    if (mate.id === player.id) continue;
    const dx = player.pos.x - mate.pos.x;
    const dy = player.pos.y - mate.pos.y;
    const d = len2(dx, dy);
    if (d > 1 && d < sep) {
      const push = (sep - d) / sep;
      x += (dx / d) * push * pushAmp;
      y += (dy / d) * push * pushAmp;
    }
  }

  const lineOffset =
    profile.line === "def"
      ? -instructions.compactness * 40
      : profile.line === "att"
        ? instructions.compactness * 55
        : 0;
  x += dir * lineOffset;

  if (team.phase === "attack") {
    x += dir * profile.attackPush * (90 + instructions.mentality * 70);
    // Referencia propia de la fase, no anclada al balon: ver ATTACK_BLOCK_PUSH.
    // `mentality` modula alrededor de 1, nunca multiplica: away la tiene
    // NEGATIVA (-0.08) y usarla como factor directo lo mandaria hacia atras.
    x += dir * profile.attackPush * TACTICS.ATTACK_BLOCK_PUSH * (1 + instructions.mentality * 0.5);
  } else if (team.phase === "defend" || team.phase === "transition") {
    const holdScale = profile.holdLine ? 0.32 : 1;
    x -= dir * profile.defendHold * holdScale * (60 + (1 - instructions.lineHeight) * 80);
  }

  // `planShape` NO pasa por `offsideAwareX` (no recibe rivales), asi que al
  // subir el bloque hay que acotarlo aqui o la gente se planta en fuera de
  // juego y el pase se anula al recibir. El que aguanta linea ya tiene su
  // propio tope justo debajo.
  if (team.phase === "attack" && !profile.holdLine) {
    const legal = team.offsideLineX - dir * TACTICS.LINE_TOLERANCE * 0.5;
    if (dir > 0) x = Math.min(x, legal);
    else x = Math.max(x, legal);
  }

  if (profile.holdLine) {
    const maxAhead = team.defensiveLineX + dir * TACTICS.LINE_TOLERANCE;
    if (dir > 0) x = Math.min(x, maxAhead);
    else x = Math.max(x, maxAhead);
  }

  if (player.tacticRole === "cb") {
    x = team.defensiveLineX + dir * 28;
  }

  return {
    x: clampX(x, player),
    y: clampY(y, player),
    arrive: profile.holdLine ? 16 : 20,
    exact: false,
  };
}

export function attackMentality(
  player: Player,
  team: TeamTacticsState,
): { passBias: number; shootBias: number; dribbleBias: number; boldness: number } {
  const { tempo, mentality } = team.instructions;
  // Tempo alto = tiki-taka: mas pase, menos regate individual.
  let passBias = 0.95 + tempo * 0.55;
  let shootBias = 1 - tempo * 0.12;
  let dribbleBias = 1.05 - tempo * 0.35;

  switch (player.tacticRole) {
    case "st":
      shootBias = 1.15 + mentality * 0.18;
      passBias *= 0.92;
      break;
    case "cm":
      passBias = 1.35 + tempo * 0.4;
      shootBias = 0.72;
      dribbleBias = 0.75;
      break;
    case "wm":
      passBias = 1.2 + tempo * 0.2;
      dribbleBias = 0.85;
      break;
    case "fb":
      passBias = 1.25;
      shootBias = 0.6;
      dribbleBias = 0.8;
      break;
    case "cb":
      passBias = 1.45;
      shootBias = 0.4;
      dribbleBias = 0.65;
      break;
    default:
      break;
  }

  // La mentalidad del equipo tambien empuja el riesgo: un plan ofensivo hace
  // que hasta el central se atreva algo mas de lo que le pide su rol.
  const boldness = roleProfile(player).boldness * (1 + mentality * 0.15);

  return { passBias, shootBias, dribbleBias, boldness };
}

function predictBallPosition(ball: Ball, t: number): Vec2 {
  const travel = (1 - dexp(-BALL.FRICTION_EFFECTIVE * t)) / BALL.FRICTION_EFFECTIVE;
  return vec(ball.pos.x + ball.vel.x * travel, ball.pos.y + ball.vel.y * travel);
}

function interceptPoint(player: Player, ball: Ball): Vec2 {
  // El torpe adivina solo parte del recorrido (`anticipationScale`): va a
  // donde esta el balon y llega tarde. Con 1 exacto, la cuenta de siempre.
  const foresight = anticipationScale(player);
  let t = dist(player.pos, ball.pos) / modeSpeed(PLAYER.MAX_SPEED);
  let point = predictBallPosition(ball, t * foresight);

  for (let i = 0; i < 2; i++) {
    t = dist(player.pos, point) / modeSpeed(PLAYER.MAX_SPEED);
    point = predictBallPosition(ball, t * foresight);
  }

  return point;
}
